// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Tithe — the sixteen tolls. A toll is a small set of LEAPS: you land on the offset square and
// anything between is irrelevant. Offsets are written from the mover's own side, (dx, dy) with
// dy = +1 FORWARD (toward the opponent) and dx = +1 to the mover's right. The far side reads the
// same printed card rotated 180°, which rules.ts does by negating both components.
//
// The deck is split into two pools of eleven, sharing a core of six. The split is not decoration —
// it is what makes Ledger a jam and Spree a raid — and it carries three structural fairness
// properties that tests/deck.test.ts re-derives by hand rather than importing:
//
//   1. Each pool has EXACTLY as many advancing offsets as retreating ones (steady 13/13,
//      wild 14/14). Neither pool charges a tempo premium for attacking, or for turtling.
//   2. Every asymmetric toll's mirror image is in the same pool (Hook/Sickle steady,
//      Crane/Heron wild), so no pool is left- or right-handed.
//   3. Exactly three tolls reach two ranks forward, and none has more than one such offset.
//      That is the hard ceiling on how fast a Keeper can be rushed. Steady holds one of them
//      (Lance); wild holds all three, which is most of why wild games are shorter.

export interface Toll {
  id: number;
  name: string;
  /** [dx, dy] pairs, from the mover's own side, dy = +1 forward. */
  off: ReadonlyArray<readonly [number, number]>;
  /**
   * A coarse "how much is this worth to hold" weight, 1–5. Used by the auto-split helper and as
   * an eval tiebreak only — never by move generation, so a wrong weight can cost you a game but
   * can never desync one.
   */
  w: number;
}

export const TOLLS: readonly Toll[] = [
  { id: 0, name: 'Crux', off: [[0, 1], [0, -1], [-1, 0], [1, 0]], w: 3 },
  { id: 1, name: 'Shears', off: [[1, 1], [-1, 1], [1, -1], [-1, -1]], w: 4 },
  { id: 2, name: 'Antler', off: [[-1, 1], [1, 1], [0, -1]], w: 4 },
  { id: 3, name: 'Recoil', off: [[0, -1], [-1, -1], [1, -1]], w: 1 },
  { id: 4, name: 'Yoke', off: [[-2, 0], [2, 0], [0, 1]], w: 3 },
  { id: 5, name: 'Lance', off: [[0, 2], [0, -1]], w: 3 },
  { id: 6, name: 'Anchor', off: [[0, 1], [-1, -1], [1, -1]], w: 3 },
  { id: 7, name: 'Hook', off: [[-1, 1], [1, 0], [0, -1]], w: 2 },
  { id: 8, name: 'Sickle', off: [[1, 1], [-1, 0], [0, -1]], w: 2 },
  { id: 9, name: 'Rake', off: [[-1, 1], [0, 1], [1, 1]], w: 4 },
  { id: 10, name: 'Tongs', off: [[-1, 0], [1, 0], [0, -1]], w: 1 },
  { id: 11, name: 'Crane', off: [[1, 2], [-1, 0], [0, -1]], w: 4 },
  { id: 12, name: 'Heron', off: [[-1, 2], [1, 0], [0, -1]], w: 4 },
  { id: 13, name: 'Kite', off: [[2, 1], [-2, 1], [0, -1]], w: 5 },
  { id: 14, name: 'Adder', off: [[0, -2], [-1, 1], [1, 1]], w: 4 },
  { id: 15, name: 'Moth', off: [[2, -1], [-2, -1], [0, 1]], w: 3 },
] as const;

/** The six every pool shares. */
export const CORE = [0, 1, 2, 3, 4, 5] as const;

export const POOLS: Record<string, readonly number[]> = {
  steady: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  wild: [0, 1, 2, 3, 4, 5, 11, 12, 13, 14, 15],
};

export const tollOf = (id: number): Toll => TOLLS[id] ?? TOLLS[0];

/** A toll id off the wire is untrusted; an out-of-range one must never reach move generation. */
export const isTollId = (id: unknown): id is number =>
  typeof id === 'number' && Number.isInteger(id) && id >= 0 && id < TOLLS.length;
